import React, { useState, useEffect } from 'react';
import {
    Search,
    Eye,
    Trash2,
    ChevronLeft,
    ChevronRight,
    MessageSquare,
    Bot,
} from 'lucide-react';

const AdminInquiryList = () => {
    const [activeTab, setActiveTab] = useState('normal');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const normalInquiries = [
        {
            id: 1,
            name: 'Rahul Sharma',
            email: 'rahul@example.com',
            message: 'I want to know more about product delivery options.',
            date: '2024-10-15',
        },
        {
            id: 2,
            name: 'Neha Gupta',
            email: 'neha@example.com',
            message: 'Can I get a discount for bulk orders?',
            date: '2024-10-16',
        },
        {
            id: 3,
            name: 'Amit Kumar',
            email: 'amit@example.com',
            message: 'Is cash on delivery available?',
            date: '2024-10-18',
        },
    ];

    const botInquiries = [
        {
            id: 1,
            user: 'Visitor123',
            question: 'What are your working hours?',
            botReply: 'We are available from 9 AM to 6 PM, Monday to Saturday.',
            date: '2024-10-20',
        },
        {
            id: 2,
            user: 'Guest456',
            question: 'Do you deliver on Sundays?',
            botReply: 'Yes, delivery is available on Sundays in select areas.',
            date: '2024-10-22',
        },
    ];

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 1200);
        return () => clearTimeout(timer);
    }, []);

    const data =
        activeTab === 'normal'
            ? normalInquiries.filter(
                (i) =>
                    i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    i.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    i.message.toLowerCase().includes(searchTerm.toLowerCase())
            )
            : botInquiries.filter(
                (i) =>
                    i.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    i.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    i.botReply.toLowerCase().includes(searchTerm.toLowerCase())
            );

    const totalPages = Math.ceil(data.length / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    const currentData = data.slice(startIndex, endIndex);

    const SkeletonRow = ({ cols }) => (
        <tr className="border-b border-gray-100">
            {[...Array(cols)].map((_, i) => (
                <td key={i} className="px-4 py-4">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                </td>
            ))}
        </tr>
    );

    return (
        <div className="min-h-screen">
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
                        <MessageSquare className="w-7 h-7 text-primaryColor" />
                        Inquiry Management
                    </h1>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-sm mb-6 flex justify-center px-4 py-3">
                    <div className="flex bg-gray-100 rounded-full p-1 w-fit">
                        <button
                            onClick={() => {
                                setActiveTab('normal');
                                setCurrentPage(1);
                                setSearchTerm('');
                            }}
                            className={`px-6 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 ${activeTab === 'normal'
                                ? 'bg-blue-500 text-white shadow-md'
                                : 'text-gray-600 hover:text-blue-600'
                                }`}
                        >
                            Normal Inquiries
                        </button>

                        <button
                            onClick={() => {
                                setActiveTab('bot');
                                setCurrentPage(1);
                                setSearchTerm('');
                            }}
                            className={`px-6 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 ${activeTab === 'bot'
                                ? 'bg-blue-500 text-white shadow-md'
                                : 'text-gray-600 hover:text-blue-600'
                                }`}
                        >
                            Bot Inquiries
                        </button>
                    </div>
                </div>
                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6 flex justify-between items-center gap-4">
                    {/* <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Show</span>
                        <select
                            value={entriesPerPage}
                            onChange={(e) => {
                                setEntriesPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </select>
                        <span className="text-sm text-gray-600">entries</span>
                    </div> */}

                    <div className="relative">
                        <input
                            type="text"
                            placeholder={`Search ${activeTab === 'normal' ? 'inquiries' : 'bot chats'
                                }...`}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full md:w-96 focus:outline-none focus:ring-1 focus:ring-primaryColor"
                        />
                        <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            {activeTab === 'normal' ? (
                                <>
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Message</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            [...Array(5)].map((_, i) => <SkeletonRow key={i} cols={5} />)
                                        ) : currentData.length > 0 ? (
                                            currentData.map((inq) => (
                                                <tr
                                                    key={inq.id}
                                                    className="border-b border-gray-100 hover:bg-gray-50 transition"
                                                >
                                                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{inq.name}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-700">{inq.email}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-700 truncate max-w-sm">{inq.message}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-600">{inq.date}</td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <button className="p-2 hover:bg-blue-50 rounded-lg transition" title="View">
                                                                <Eye className="w-4 h-4 text-[#ea8d2a]" />
                                                            </button>
                                                            <button className="p-2 hover:bg-red-50 rounded-lg transition" title="Delete">
                                                                <Trash2 className="w-4 h-4 text-red-500" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                                    No inquiries found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </>
                            ) : (
                                <>
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">User</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Question</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Bot Reply</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                                            <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            [...Array(5)].map((_, i) => <SkeletonRow key={i} cols={5} />)
                                        ) : currentData.length > 0 ? (
                                            currentData.map((inq) => (
                                                <tr
                                                    key={inq.id}
                                                    className="border-b border-gray-100 hover:bg-gray-50 transition"
                                                >
                                                    <td className="px-4 py-4 text-sm font-medium text-gray-900 flex items-center gap-2">
                                                        <Bot className="w-4 h-4 text-blue-500" />
                                                        {inq.user}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-gray-700 truncate max-w-sm">{inq.question}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-700 truncate max-w-sm">{inq.botReply}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-600">{inq.date}</td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <button className="p-2 hover:bg-blue-50 rounded-lg transition" title="View">
                                                                <Eye className="w-4 h-4 text-[#ea8d2a]" />
                                                            </button>
                                                            <button className="p-2 hover:bg-red-50 rounded-lg transition" title="Delete">
                                                                <Trash2 className="w-4 h-4 text-red-500" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                                    No bot inquiries found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </>
                            )}
                        </table>
                    </div>

                    {/* Pagination */}
                    {!loading && data.length > 0 && (
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Showing {startIndex + 1} to {Math.min(endIndex, data.length)} of {data.length} records
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>

                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentPage(i + 1)}
                                        className={`px-4 py-2 rounded-lg transition ${currentPage === i + 1
                                            ? 'bg-blue-500 text-white'
                                            : 'border border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminInquiryList;
